import { inject, Injectable } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { BehaviorSubject, distinctUntilChanged, map } from 'rxjs'
import { injectMutation, injectQuery, injectQueryClient } from '@ngneat/query'
import { HttpClient } from '@angular/common/http'
import { DemoProfile, DemoProfileDto } from './users.service'
import { queryKeys } from './query-keys'

@Injectable({
  providedIn: 'root',
})
export class DemoProfilesService {
  query = injectQuery()
  queryClient = injectQueryClient()
  mutation = injectMutation()
  route = inject(ActivatedRoute)
  httpClient = inject(HttpClient)

  activeDemoProfileId$ = new BehaviorSubject<number | null>(null)

  constructor() {
    this.route.queryParams
      .pipe(
        map((queryParams) => (typeof queryParams['demoProfileId'] === 'string' ? parseInt(queryParams['demoProfileId']) : null)),
        distinctUntilChanged()
      )
      .subscribe(this.activeDemoProfileId$)
  }

  getAll() {
    return this.query({
      queryKey: queryKeys.demoProfiles.base,
      queryFn: () => this.httpClient.get<DemoProfile[]>('/api/users/me/demo-profiles'),
    }).result$
  }

  getOne(id: number, receiveUpdates = false) {
    return this.query({
      queryKey: queryKeys.demoProfiles.byId(id),
      queryFn: () => this.httpClient.get<DemoProfile>(`/api/users/me/demo-profiles/${id}`),
      refetchOnMount: receiveUpdates,
      refetchOnReconnect: receiveUpdates,
      refetchOnWindowFocus: receiveUpdates,
    }).result$
  }

  create() {
    return this.mutation({
      mutationFn: (demoProfile: DemoProfileDto) => this.httpClient.post<DemoProfile>('/api/users/me/demo-profiles', demoProfile),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.demoProfiles.base }),
    })
  }

  update() {
    return this.mutation({
      mutationFn: ({ id, demoProfile }: { id: number; demoProfile: DemoProfileDto }) =>
        this.httpClient.put<DemoProfile>(`/api/users/me/demo-profiles/${id}`, demoProfile),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.demoProfiles.base }),
    })
  }

  delete() {
    return this.mutation({
      mutationFn: (id: number) => this.httpClient.delete<void>(`/api/users/me/demo-profiles/${id}`),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: queryKeys.demoProfiles.base }),
    })
  }
}
